import React, { useState, useCallback, useRef } from 'react';
import { FileData } from '../../types/training';
import FileItem from './FileItem';
import { processDroppedItem } from '../../utils/training';
import EnhancedFilePreview from './EnhancedFilePreview';
import './FileBox.css';

interface FileBoxProps {
  id: string;
  title: string;
  files: FileData[];
  onFilesAdd: (boxId: string, newFiles: FileData[]) => void;
  onFileClick: (event: React.MouseEvent<HTMLDivElement>, file: FileData, boxId: string) => void;
  onFileUpdate?: (boxId: string, updatedFile: FileData) => void;
  className?: string;
}

const FileBox: React.FC<FileBoxProps> = ({ id, title, files, onFilesAdd, onFileClick, onFileUpdate, className = '' }) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(true);
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setIsDraggingOver(false);
    }
  }, []);

  const handleDrop = useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingOver(false);

    const droppedItems = event.dataTransfer.items;
    const newFiles: FileData[] = [];

    if (droppedItems) {
      const processingPromises: Promise<FileData | null>[] = [];
      for (let i = 0; i < droppedItems.length; i++) {
        processingPromises.push(processDroppedItem(droppedItems[i]));
      }
      const results = await Promise.all(processingPromises);
      results.forEach(fileData => {
        if (fileData) {
          newFiles.push(fileData);
        }
      });
    } else {
      const droppedFiles = event.dataTransfer.files;
      for (let i = 0; i < droppedFiles.length; i++) {
        const file = droppedFiles[i];
        console.warn("Using fallback file drop handling.");
      }
    }

    if (newFiles.length > 0) {
      onFilesAdd(id, newFiles);
    }
  }, [onFilesAdd, id]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = event.target.files;
    if (uploadedFiles) {
      console.warn("File processing from input not fully implemented here.");
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleInternalFileClick = (event: React.MouseEvent<HTMLDivElement>, file: FileData) => {
    // Show context menu on left-click
    onFileClick(event, file, id);
  };

  return (
    <div className={`file-box ${className}`}>
      <h2 className="file-box-title">
        {title}
      </h2>
      <div
        className={`file-box-content ${isDraggingOver ? 'dragging-over' : ''}`}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDraggingOver && (
          <div className="file-box-drop-overlay">
            <span className="file-box-drop-text">Drop files here</span>
          </div>
        )}

        {files.length === 0 && !isDraggingOver && (
          <div className="file-box-empty">
            <p>Drag & drop files/folders/text here</p>
            <p className="file-box-empty-or">or</p>
            <button
              onClick={handleUploadClick}
              className="file-box-upload-btn"
            >
              Upload Files
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              multiple
            />
          </div>
        )}

        {files.length > 0 && (
          <div className="file-box-count">
            {files.length} {files.length === 1 ? 'file' : 'files'}
            {files.length > 15 && <span className="file-box-count-warning">(scroll to see all files)</span>}
          </div>
        )}

        <div className="file-box-list">
          <div className="file-box-grid">
            {files.map((file) => (
              <FileItem key={file.id} file={file} onClick={handleInternalFileClick} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FileBox;

